// src/api/studentsApi.js
import { api } from "./client";

/**
 * GOAL
 * - Make filters "just work" no matter which backend endpoint we're hitting:
 *   /api/scholars, /api/students, or roster students endpoint.
 * - Support search (q/search), house_id (preferred), house/house_name (fallback),
 *   grade (grade/grade_level as number), roster_id passthrough.
 * - If the server ignores filters, apply them client-side as a safety net.
 * - Provide consistent pagination.
 *
 * Backend refactor notes:
 * - Canonical list endpoints now exist at:
 *     GET /api/students/
 *     GET /api/scholars/
 * - Roster membership exists at:
 *     GET /api/rosters/<id>/students/
 * - The old reports students feed (/api/reports/students/) is NOT present anymore.
 */

// ---------- helpers ----------

// Grade normalizer: "1", "01", "1st", "Grade 1" -> 1; returns null if not a valid grade 0..12
function normalizeGrade(input) {
  if (input === null || input === undefined) return null;
  const raw = String(input).trim();
  // fast path for pure integers
  if (/^\d+$/.test(raw)) {
    const n = Number(raw);
    if (Number.isNaN(n)) return null;
    if (n < 0 || n > 12) return null; // adjust range if your school differs
    return n;
  }
  // strip non-digits (handles "1st", "Grade 1", "gr 01", etc.)
  const digits = raw.replace(/[^0-9]/g, "");
  if (!digits) return null;
  const n = Number(digits);
  if (Number.isNaN(n)) return null;
  if (n < 0 || n > 12) return null;
  return n;
}

async function safeGet(path, opts = {}) {
  try {
    const res = await api.get(path, opts);
    return res || { ok: false, status: 0, data: null };
  } catch (e) {
    return { ok: false, status: 0, data: { detail: e?.message || "Network error" } };
  }
}

function toArray(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  // Sometimes results can be nested differently:
  if (payload?.results?.data && Array.isArray(payload.results.data)) return payload.results.data;
  return [];
}

function serverCount(payload, fallback = 0) {
  if (!payload) return fallback;
  if (typeof payload?.count === "number") return payload.count;
  if (typeof payload?.total === "number") return payload.total;
  return fallback;
}

function normalizeName(row) {
  if (row.name) return row.name;
  const fn = row.first_name || row.firstName || "";
  const ln = row.last_name || row.lastName || "";
  const n = `${fn} ${ln}`.trim();
  return n || row.email || "";
}
function normalizeHouse(row) {
  if (row.house) {
    if (typeof row.house === "string") return row.house;
    if (row.house && row.house.name) return row.house.name;
  }
  return row.house_name || "";
}
function normalizeHouseId(row) {
  if (row.house_id != null && row.house_id !== "") {
    const n = Number(row.house_id);
    return Number.isNaN(n) ? null : n;
  }
  if (row.house && row.house.id != null) {
    const n = Number(row.house.id);
    return Number.isNaN(n) ? null : n;
  }
  // Some feeds may use scholar.houseId
  if (row.houseId != null) {
    const n = Number(row.houseId);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}
function normalizeOne(row = {}) {
  return {
    id:
      row.id ?? row.pk ?? row.uuid ?? row.scholar_id ?? row.student_id ??
      null,
    name: normalizeName(row),
    house: normalizeHouse(row),
    house_id: normalizeHouseId(row),
    grade: row.grade ?? row.grade_level ?? null, // keep raw; we'll normalize when filtering
    points: row.points ?? null,
    raw: row,
  };
}

// Build multiple query variants so different APIs match.
// NOTE: now supports house_id (preferred by backend).
function buildQueryVariants(params = {}) {
  const {
    // preferred
    house_id,

    // legacy/fallbacks
    q,
    search,
    house,
    house_name,
    grade,
    grade_level,
    roster_id,
    page,
    page_size,
    limit,
  } = params;

  const text = (q ?? search ?? "").toString().trim();
  const gRaw = (grade ?? grade_level);
  const gNum = normalizeGrade(gRaw);
  const hasValidGrade = gNum !== null;

  const houseRaw = (house ?? house_name ?? "").toString().trim();
  const H = houseRaw.toUpperCase();
  const h = houseRaw.toLowerCase();

  // pagination variants (some backends use `limit`)
  const basePage = { page: page ?? 1, page_size: page_size ?? limit ?? 50 };
  const baseAltPage = { page: page ?? 1, limit: page_size ?? limit ?? 50 };

  // common fields across variants
  const commonFilters = [];

  if (text) commonFilters.push([{ q: text }, { search: text }]);

  // FIRST: push house_id if provided (canonical)
  const hid = house_id == null || house_id === "" ? null : Number(house_id);
  const hasHouseId = hid !== null && !Number.isNaN(hid);
  if (hasHouseId) {
    commonFilters.push([{ house_id: String(hid) }]);
  }

  // THEN: legacy house by name (case-insensitive variants)
  if (houseRaw) {
    commonFilters.push([
      { house: houseRaw }, { house_name: houseRaw },
      { house: H }, { house_name: H },
      { house: h }, { house_name: h },
    ]);
  }

  if (hasValidGrade) {
    // send numeric grade to both shapes
    commonFilters.push([{ grade: gNum }, { grade_level: gNum }]);
  }

  // roster passthrough (if supported by list endpoint)
  if (roster_id != null && roster_id !== "") {
    commonFilters.push([{ roster_id }]);
  }

  // If no filters at all, still build a minimal variant for pagination
  if (commonFilters.length === 0) {
    return [
      basePage,
      baseAltPage,
    ];
  }

  // Cartesian-ish expansion across filter sets + pagination shapes
  const variants = [];
  const expand = (arrs, sofar = {}) => {
    if (arrs.length === 0) {
      variants.push({ ...sofar, ...basePage });
      variants.push({ ...sofar, ...baseAltPage });
      return;
    }
    const [head, ...tail] = arrs;
    for (const opt of head) {
      expand(tail, { ...sofar, ...opt });
    }
  };
  expand(commonFilters);
  return variants;
}

// Client-side filter as a safety net
function applyClientFilters(items, params) {
  const text = (params.q ?? params.search ?? "").toString().trim().toLowerCase();

  // Respect numeric house_id if present (preferred)
  const hidRaw = params.house_id;
  const hid =
    hidRaw === undefined || hidRaw === null || hidRaw === ""
      ? null
      : Number(hidRaw);

  const houseRaw = (params.house ?? params.house_name ?? "").toString().trim().toUpperCase();
  const gRaw = (params.grade ?? params.grade_level);
  const gNum = normalizeGrade(gRaw);

  let out = items;

  if (text) {
    out = out.filter((s) => s.name.toLowerCase().includes(text));
  }

  if (hid !== null && !Number.isNaN(hid)) {
    out = out.filter((s) => Number(s.house_id) === hid);
  } else if (houseRaw) {
    out = out.filter((s) => String(s.house || "").trim().toUpperCase() === houseRaw);
  }

  if (gNum !== null) {
    out = out.filter((s) => normalizeGrade(s.grade) === gNum);
  }

  return out;
}

function paginate(items, page = 1, pageSize = 50) {
  const start = (Math.max(1, Number(page)) - 1) * Math.max(1, Number(pageSize));
  return items.slice(start, start + pageSize);
}

// ---------- public API ----------
export const studentsApi = {
  /**
   * List students with filters.
   * Params:
   * {
   *   q?, search?,
   *   house_id?,            // preferred numeric id
   *   house?, house_name?,  // legacy name filters
   *   grade?, grade_level?,
   *   roster_id?,
   *   page?, page_size?
   * }
   * Returns: { ok, status, students[], count, raw }
   */
  async list(params = {}) {
    const variants = buildQueryVariants(params);

    const hasFilters = !!(
      params.q ||
      params.search ||
      params.house ||
      params.house_name ||
      params.house_id ||
      params.grade ||
      params.grade_level ||
      params.roster_id
    );

    const page = params.page ?? 1;
    const pageSize = params.page_size ?? params.limit ?? 50;

    const tryEndpoint = async (path) => {
      for (const qv of variants) {
        const res = await safeGet(path, { query: qv });
        if (!res.ok) continue;

        const rawItems = toArray(res.data);
        const normalized = rawItems.map(normalizeOne).filter((s) => s.id && s.name);

        // Apply client-side filters as safety net
        const filtered = applyClientFilters(normalized, params);

        // Prefer server's total if provided; else use filtered length.
        const total = serverCount(res.data, filtered.length);

        // Consistent client-side pagination
        const paged = paginate(filtered, page, pageSize);

        // If filters were requested but nothing matched, try next variant/path
        if (hasFilters && filtered.length === 0) continue;

        return {
          ok: true,
          status: res.status,
          students: paged,
          count: total,
          raw: res.data,
        };
      }
      return null;
    };

    // 1) Prefer canonical endpoints first (refactored backend)
    let out =
      (await tryEndpoint("/api/students/")) ||
      (await tryEndpoint("/api/scholars/")) ||
      // allow fallback without slash if deployed oddly
      (await tryEndpoint("/api/students")) ||
      (await tryEndpoint("/api/scholars"));

    // 2) If roster_id is present and list endpoints didn't help, try roster membership endpoint directly.
    // This endpoint is part of the refactored backend:
    //   GET /api/rosters/<id>/students/
    if (!out && params.roster_id != null && params.roster_id !== "") {
      const rid = String(params.roster_id);
      const rosterRes =
        (await safeGet(`/api/rosters/${rid}/students/`)) ||
        (await safeGet(`/api/rosters/${rid}/students`));

      if (rosterRes.ok) {
        const rawItems = toArray(rosterRes.data);
        const normalized = rawItems.map(normalizeOne).filter((s) => s.id && s.name);

        const filtered = applyClientFilters(normalized, params);
        const paged = paginate(filtered, page, pageSize);

        out = {
          ok: true,
          status: rosterRes.status,
          students: paged,
          count: filtered.length,
          raw: rosterRes.data,
        };
      }
    }

    // 3) If still no response, return a clean error (reports feed no longer exists)
    if (!out) {
      return {
        ok: false,
        status: 404,
        students: [],
        count: 0,
        raw: null,
        error: "Failed to load students (no compatible endpoint responded).",
      };
    }

    return out;
  },
};
